import { API_CONFIG } from '../config/api';
import type { 
  Category, 
  CreateCategoryData, 
  UpdateCategoryData
  // Removed CategoriesResponse and CategoryResponse
} from '../types/category';

// Get all categories
export const getAllCategories = async (): Promise<Category[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/categories`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch categories:', response.status, errorText);
      throw new Error(`Failed to fetch categories: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Failed to fetch categories');
  }
};

// Create a new category
export const createCategory = async (categoryData: CreateCategoryData): Promise<Category> => {
  try {
    console.log('Creating category with data:', categoryData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(categoryData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Category creation failed:', response.status, errorText);
      
      let errorMessage = 'Failed to create category';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Category created successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

// Update a category
export const updateCategory = async (categoryData: UpdateCategoryData): Promise<Category> => {
  try {
    console.log('Updating category with data:', categoryData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/categories/${categoryData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        name: categoryData.name,
        description: categoryData.description,
        status: categoryData.status,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Category update failed:', response.status, errorText);
      
      let errorMessage = 'Failed to update category';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Category updated successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

// Delete a category
export const deleteCategory = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Category deletion failed:', response.status, errorText);
      
      let errorMessage = 'Failed to delete category';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Category deleted successfully');
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};